import React from 'react';

export interface ActiveUser {
  username: string;
  role: string;
  last_seen_seconds_ago: number;
  current_cantiere_id?: number | null;
  current_cantiere_name?: string | null;
  current_action?: string;
}

interface ActiveUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: ActiveUser[];
  activeCount: number;
  isLoading: boolean;
  onRefresh: () => void;
}

export const formatLastSeen = (seconds: number): string => {
  if (seconds < 15) return 'In questo momento';
  if (seconds < 60) return `${seconds}s fa`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m fa`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h fa`;
};

const getRoleBadge = (role: string) => {
  switch (role?.toLowerCase()) {
    case 'admin':
      return (
        <span style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          Admin
        </span>
      );
    case 'operator':
      return (
        <span style={{
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          Operatore
        </span>
      );
    case 'client':
      return (
        <span style={{
          backgroundColor: '#ede7f6',
          color: '#512da8',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          Cliente
        </span>
      );
    default:
      return (
        <span style={{
          backgroundColor: '#f5f5f5',
          color: '#616161',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          {role || 'Utente'}
        </span>
      );
  }
};

const ActiveUsersModal: React.FC<ActiveUsersModalProps> = ({
  isOpen,
  onClose,
  users,
  activeCount,
  isLoading,
  onRefresh,
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '750px', width: '90%' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              boxShadow: '0 0 6px rgba(76, 175, 80, 0.8)'
            }}></span>
            <h2 style={{ margin: 0, fontSize: '1.4rem' }}>
              Utenti Attivi ({activeCount})
            </h2>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={onRefresh}
              disabled={isLoading}
              title="Aggiorna lista"
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
              <span>Aggiorna</span>
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.4rem',
                cursor: 'pointer',
                color: '#666',
                padding: '4px 8px'
              }}
              title="Chiudi"
            >
              ✕
            </button>
          </div>
        </div>

        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '16px' }}>
          Sessioni con attività negli ultimi 10 minuti. Il dettaglio delle attività è riservato ad amministratori e operatori.
        </p>

        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
            Nessun utente attivo registrato al momento.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', fontSize: '0.85rem', color: '#555' }}>
                  <th style={{ padding: '10px 12px' }}>Utente</th>
                  <th style={{ padding: '10px 12px' }}>Ruolo</th>
                  <th style={{ padding: '10px 12px' }}>Cantiere</th>
                  <th style={{ padding: '10px 12px' }}>Attività / Posizione</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Ultimo Segnale</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa'
                    }}
                  >
                    <td style={{ padding: '12px', fontWeight: 500 }}>
                      <i className="fas fa-user-circle" style={{ marginRight: '8px', color: '#888' }}></i>
                      {u.username}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {getRoleBadge(u.role)}
                    </td>
                    <td style={{ padding: '12px', color: u.current_cantiere_name ? '#1976d2' : '#999' }}>
                      {u.current_cantiere_name ? (
                        <span>
                          <i className="fas fa-folder-open" style={{ marginRight: '6px', fontSize: '0.85rem' }}></i>
                          {u.current_cantiere_name}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.9rem' }}>
                      <span style={{
                        color: '#333',
                        backgroundColor: '#f5f5f5',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}>
                        {u.current_action || 'Attivo'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '0.85rem', color: '#777' }}>
                      {formatLastSeen(u.last_seen_seconds_ago)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '24px', textAlign: 'right' }}>
          <button className="btn btn-primary" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveUsersModal;
